import { Box } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { useMeasuredWidth, niceTicks } from './chartScale';
import { useUsageSeriesColors } from './useUsageSeriesColors';

export interface AreaTrendPoint {
  /** X-axis label (e.g. a month, "Jul 2026"). */
  label: string;
  /** Filled-area series value. */
  actual: number;
  /** Dashed reference-line series value. */
  budget: number;
}

export interface AreaTrendChartProps {
  points: AreaTrendPoint[];
  /** Plot height in px (matches the container `ChartCard` height). */
  height?: number;
  /** Y-axis tick formatter (defaults to a locale integer). */
  formatValue?: (value: number) => string;
}

/**
 * Actual-vs-budget trend: a filled area under the actual line plus a dashed
 * budget reference line, with nice Y ticks and per-point X labels. Pure SVG,
 * responsive via {@link useMeasuredWidth}, mode-aware colors. Plot only — wrap
 * it in a {@link ChartCard} and pass the legend (Actual / Budgeted) there. The
 * single most-reused chart across the dashboard, reports, and line detail.
 */
export function AreaTrendChart({ points, height = 250, formatValue }: AreaTrendChartProps) {
  const { mode, systemMode } = useColorScheme();
  const isDark = (mode === 'system' ? systemMode : mode) === 'dark';
  const [ref, width] = useMeasuredWidth();

  const fmt = formatValue ?? ((v: number) => v.toLocaleString());
  const padL = 60;
  const padR = 16;
  const padT = 12;
  const padB = 28;
  const innerW = Math.max(0, width - padL - padR);
  const innerH = height - padT - padB;

  const actual = points.map((p) => p.actual);
  const budget = points.map((p) => p.budget);
  const maxVal = Math.max(0, ...actual, ...budget);
  const { ticks, niceMax } = niceTicks(maxVal, 5);
  const n = points.length;
  const stepX = n > 1 ? innerW / (n - 1) : innerW;
  const y = (v: number) => padT + innerH - (v / niceMax) * innerH;

  const actualPts = actual.map((v, i) => [padL + i * stepX, y(v)] as const);
  const budgetPts = budget.map((v, i) => [padL + i * stepX, y(v)] as const);
  const toPath = (pts: readonly (readonly [number, number])[]) =>
    pts.map(([x, yy], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${yy.toFixed(1)}`).join(' ');

  const grid = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(4,29,51,0.06)';
  const axis = isDark ? 'rgba(255,255,255,0.65)' : '#435666';
  const { actualColor, budgetColor } = useUsageSeriesColors();
  const dotStroke = isDark ? '#142639' : '#FFFFFF';

  return (
    <Box ref={ref} sx={{ width: '100%', height: '100%' }}>
      {width > 0 && n > 0 && (
        <svg width={width} height={height} style={{ display: 'block', fontFamily: 'inherit' }}>
          {ticks.map((t, i) => {
            const yy = y(t);
            return (
              <g key={i}>
                <line
                  x1={padL}
                  y1={yy}
                  x2={padL + innerW}
                  y2={yy}
                  stroke={grid}
                  strokeDasharray={i === 0 ? undefined : '2 4'}
                />
                <text
                  x={padL - 8}
                  y={yy + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill={axis}
                  fontWeight="500"
                >
                  {fmt(t)}
                </text>
              </g>
            );
          })}

          <path
            d={`${toPath(actualPts)} L ${actualPts[n - 1]![0]} ${padT + innerH} L ${actualPts[0]![0]} ${padT + innerH} Z`}
            fill={actualColor}
            opacity={isDark ? 0.22 : 0.18}
          />
          <path d={toPath(actualPts)} fill="none" stroke={actualColor} strokeWidth="2" />
          <path
            d={toPath(budgetPts)}
            fill="none"
            stroke={budgetColor}
            strokeWidth="1.5"
            strokeDasharray="6 5"
          />

          {points.map((p, i) => (
            <text
              key={i}
              x={padL + i * stepX}
              y={height - 8}
              textAnchor="middle"
              fontSize="11"
              fill={axis}
              fontWeight="500"
            >
              {p.label}
            </text>
          ))}

          <circle
            cx={actualPts[n - 1]![0]}
            cy={actualPts[n - 1]![1]}
            r="3.5"
            fill={actualColor}
            stroke={dotStroke}
            strokeWidth="1.5"
          />
        </svg>
      )}
    </Box>
  );
}
